import { useState, useRef, useEffect } from 'react'
import { Stack, Title, Card, TextInput, Button, ScrollArea, Group, Text, Paper, Loader } from '@mantine/core'
import { IconSend, IconRobot, IconUser } from '@tabler/icons-react'
import { useMutation } from '@tanstack/react-query'
import { assistantApi } from '@/api'
import { notifications } from '@mantine/notifications'
import type { AssistantResponse } from '@/types'

interface Message {
  id: string
  type: 'user' | 'assistant'
  content: string
  response?: AssistantResponse
  timestamp: Date
}

export function Assistant() {
  const [messages, setMessages] = useState<Message[]>([])
  const [input, setInput] = useState('')
  const scrollAreaRef = useRef<HTMLDivElement>(null)

  const assistantMutation = useMutation({
    mutationFn: async (prompt: string) => {
      const { data } = await assistantApi.query(prompt)
      return data
    },
    onSuccess: (data, prompt) => {
      setMessages((prev) => [
        ...prev,
        {
          id: Date.now().toString(),
          type: 'assistant',
          content: data.explanation,
          response: data,
          timestamp: new Date(),
        },
      ])
    },
    onError: () => {
      notifications.show({
        title: 'Error',
        message: 'Failed to get response from assistant',
        color: 'red',
      })
    },
  })

  const sendMessage = () => {
    if (!input.trim()) return

    const userMessage: Message = {
      id: Date.now().toString(),
      type: 'user',
      content: input,
      timestamp: new Date(),
    }

    setMessages((prev) => [...prev, userMessage])
    assistantMutation.mutate(input)
    setInput('')
  }

  useEffect(() => {
    // Scroll to bottom when new messages arrive
    if (scrollAreaRef.current) {
      scrollAreaRef.current.scrollTop = scrollAreaRef.current.scrollHeight
    }
  }, [messages])

  return (
    <Stack gap="lg" h="calc(100vh - 120px)">
      <Title order={2}>AI Operations Assistant</Title>

      <Card shadow="sm" p="lg" radius="md" h="100%" style={{ display: 'flex', flexDirection: 'column' }}>
        <ScrollArea
          flex={1}
          viewportRef={scrollAreaRef}
          styles={{
            viewport: {
              paddingBottom: '1rem',
            },
          }}
        >
          <Stack gap="md" p="md">
            {messages.length === 0 && (
              <Paper p="xl" withBorder style={{ textAlign: 'center' }}>
                <IconRobot size={48} style={{ marginBottom: '1rem' }} />
                <Text size="lg" fw={500} mb="xs">
                  Hello! I'm your AI Operations Assistant
                </Text>
                <Text c="dimmed">
                  Ask me anything about your cluster, services, or performance.
                </Text>
                <Text size="sm" c="dimmed" mt="md">
                  Try: "Why is my API slow?" or "Scale web service to handle traffic"
                </Text>
              </Paper>
            )}

            {messages.map((message) => (
              <Group key={message.id} align="flex-start" gap="sm">
                {message.type === 'user' ? (
                  <>
                    <IconUser size={24} />
                    <Paper p="md" withBorder flex={1}>
                      <Text>{message.content}</Text>
                    </Paper>
                  </>
                ) : (
                  <>
                    <IconRobot size={24} />
                    <Paper p="md" withBorder flex={1} bg="blue.0">
                      <Text>{message.content}</Text>

                      {message.response?.insights && message.response.insights.length > 0 && (
                        <Stack gap="xs" mt="md">
                          <Text size="sm" fw={600}>Insights:</Text>
                          {message.response.insights.map((insight, i) => (
                            <Paper key={i} p="sm" withBorder>
                              <Text size="sm" fw={500}>{insight.message}</Text>
                              <Text size="xs" c="dimmed">{insight.category} - {insight.severity}</Text>
                            </Paper>
                          ))}
                        </Stack>
                      )}

                      {message.response?.suggested_actions && message.response.suggested_actions.length > 0 && (
                        <Stack gap="xs" mt="md">
                          <Text size="sm" fw={600}>Suggested Actions:</Text>
                          {message.response.suggested_actions.map((action, i) => (
                            <Paper key={i} p="sm" withBorder>
                              <Group justify="space-between">
                                <Text size="sm">{action.description}</Text>
                                <Text size="xs" c="dimmed">
                                  {(action.confidence * 100).toFixed(0)}% confidence
                                </Text>
                              </Group>
                              <Text size="xs" c="dimmed">{action.impact.description}</Text>
                            </Paper>
                          ))}
                        </Stack>
                      )}
                    </Paper>
                  </>
                )}
              </Group>
            ))}

            {assistantMutation.isPending && (
              <Group align="flex-start" gap="sm">
                <IconRobot size={24} />
                <Paper p="md" withBorder flex={1} bg="blue.0">
                  <Group gap="xs">
                    <Loader size="sm" />
                    <Text>Thinking...</Text>
                  </Group>
                </Paper>
              </Group>
            )}
          </Stack>
        </ScrollArea>

        <Group mt="md">
          <TextInput
            flex={1}
            placeholder="Ask me anything about your cluster..."
            value={input}
            onChange={(e) => setInput(e.currentTarget.value)}
            onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
            rightSection={
              <Button
                size="xs"
                onClick={sendMessage}
                loading={assistantMutation.isPending}
                disabled={!input.trim()}
              >
                <IconSend size={16} />
              </Button>
            }
          />
        </Group>
      </Card>
    </Stack>
  )
}